import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { assetsService, type AssetStatus } from '@/services/assets.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues {
  name: string;
  code: string;
  color: string;
  display_order: number;
  is_default: boolean;
}

function toStatusCode(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  status?: AssetStatus;
  onClose: () => void;
  onSuccess: () => void;
}

export function StatusFormDialog({ open, mode, status, onClose, onSuccess }: Props) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      name: '',
      code: '',
      color: '#6b7280',
      display_order: 0,
      is_default: false,
    },
  });

  const color = watch('color');

  useEffect(() => {
    if (open) {
      reset({
        name: status?.name ?? '',
        code: status?.code ?? '',
        color: status?.color ?? '#6b7280',
        display_order: status?.display_order ?? 0,
        is_default: status?.is_default ?? false,
      });
    }
  }, [open, status, reset]);

  // Auto-set code from name when in create mode
  const name = watch('name');
  useEffect(() => {
    if (!isEdit) {
      setValue('code', toStatusCode(name));
    }
  }, [name, isEdit, setValue]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const input = {
        name: values.name.trim(),
        code: values.code.trim(),
        color: values.color,
        display_order: Number(values.display_order),
        is_default: values.is_default,
      };
      if (isEdit && status) {
        await assetsService.updateStatus(status.id, input);
      } else {
        await assetsService.createStatus(input);
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Status updated' : 'Status created');
      void queryClient.invalidateQueries({ queryKey: ['assets', 'statuses'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Status' : 'New Status'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="status-name">Name *</Label>
            <Input
              id="status-name"
              {...register('name', { required: 'Name is required' })}
              placeholder="e.g. In Stock"
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="status-code">Code *</Label>
            <Input
              id="status-code"
              {...register('code', { required: 'Code is required' })}
              placeholder="e.g. in_stock"
            />
            {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={color}
                onChange={(e) => {
                  setValue('color', e.target.value);
                }}
                className="h-9 w-14 rounded cursor-pointer border border-input bg-background p-0.5"
                aria-label="Pick color"
              />
              <Input
                value={color}
                onChange={(e) => {
                  setValue('color', e.target.value);
                }}
                placeholder="#000000"
                className="flex-1"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="status-order">Display Order</Label>
            <Input
              id="status-order"
              type="number"
              {...register('display_order', { valueAsNumber: true })}
              placeholder="0"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="status-default"
              {...register('is_default')}
            />
            <Label htmlFor="status-default" className="cursor-pointer font-normal">
              Set as default status for new assets
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
